import React, { useState, useRef, useEffect } from 'react';
import { API, COLORS } from '../utils/constants';
import { r1, compressImage } from '../utils/helpers';
import ImagePicker from '../components/ImagePicker';
import MaximizedPopup from '../components/MaximizedPopup';
import Popup from '../components/Popup';
import SwipeableRow from '../components/SwipeableRow';
import AlimentoWizard from '../components/AlimentoWizard';


export default function RecipesScreen() {
  const [recetas, setRecetas] = useState([]);
  const [verReceta, setVerReceta] = useState(null);
  const [expandedMacroRow, setExpandedMacroRow] = useState(null);
  const [editandoReceta, setEditandoReceta] = useState(null);
  const [toast, setToast] = useState("");
  const [busquedaRecetas, setBusquedaRecetas] = useState("");

  // Crear receta
  const [creating, setCreating] = useState(false);
  const [nombre, setNombre] = useState("");
  const [ingredientes, setIngredientes] = useState([]);
  const [paso, setPaso] = useState("input");
  const [numPorciones, setNumPorciones] = useState("1");
  const [tipoRegistro, setTipoRegistro] = useState("porciones"); // "porciones" | "gramos"
  const [showWizardManual, setShowWizardManual] = useState(false);

  // Panel busqueda (crear)
  const [ingQuery, setIngQuery] = useState("");
  const [ingResults, setIngResults] = useState([]);
  const ingSearchTimeout = useRef(null);
  const lastIngRef = useRef(null);
  const lastCantidadRef = useRef(null);
  const ingInputRef = useRef(null);
  const hiddenNumRef = useRef(null);
  const [shouldFocusCantidad, setShouldFocusCantidad] = useState(false);

  // Wizard para ingrediente faltante (rojo)
  const [missingLabelIdx, setMissingLabelIdx] = useState(null);
  // Confirmar alimento encontrado en internet
  const [scanConfirmReceta, setScanConfirmReceta] = useState(null);
  const [editScanConfirm, setEditScanConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Preparación y metadata extra del scan
  const [preparacion, setPreparacion] = useState("");
  const [metadataExtra, setMetadataExtra] = useState({});
  const [panelPrep, setPanelPrep] = useState(false);
  const [editandoPreparacion, setEditandoPreparacion] = useState(null);
  const [prepEditText, setPrepEditText] = useState("");
  const [prepScanLoading, setPrepScanLoading] = useState(false);

  // Editar receta
  const [editIngQuery, setEditIngQuery] = useState("");
  const [editIngResults, setEditIngResults] = useState([]);
  const editIngTimeout = useRef(null);
  const [editIngEditIdx, setEditIngEditIdx] = useState(null);
  const [editIngEditQuery, setEditIngEditQuery] = useState("");
  const [editIngEditResults, setEditIngEditResults] = useState([]);
  const editIngEditTimeout = useRef(null);

  const ingSuggestionsRef = useRef(null);
  const editSuggestionsRef = useRef(null);

  const scrollToSuggestions = (ref) => {
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const container = document.querySelector('[data-maxpopup="true"]');
      if (!container) return;
      const elRect = ref.current.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();
      if (elRect.bottom > contRect.bottom - 8) {
        container.scrollTop += (elRect.bottom - contRect.bottom) + 16;
      }
    });
  };

  useEffect(() => { if (ingResults.length > 0) scrollToSuggestions(ingSuggestionsRef); }, [ingResults.length]);
  useEffect(() => { if (editIngResults.length > 0) scrollToSuggestions(editSuggestionsRef); }, [editIngResults.length]);

  // Auto-scroll al último ingrediente añadido
  useEffect(() => {
    if (ingredientes.length > 0 && lastIngRef.current) {
      setTimeout(() => {
        lastIngRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    }
  }, [ingredientes.length]);

  // Auto-focus campo cantidad tras añadir ingrediente desde sugerencia
  useEffect(() => {
    if (shouldFocusCantidad && lastCantidadRef.current) {
      requestAnimationFrame(() => {
        lastCantidadRef.current?.focus();
        setShouldFocusCantidad(false);
      });
    }
  }, [ingredientes.length, shouldFocusCantidad]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };
  const sortIngs = (ings) => [...ings].sort((a, b) => b.gramos !== a.gramos ? b.gramos - a.gramos : a.nombre.localeCompare(b.nombre));

  const highlightMatch = (text, query) => {
    if (!query || query.length < 2) return <span style={{ color: COLORS.text }}>{text}</span>;
    const lowerText = text.toLowerCase();
    const idx = lowerText.indexOf(query.toLowerCase());
    if (idx === -1) return <span style={{ color: COLORS.text }}>{text}</span>;
    const wordStart = lowerText.lastIndexOf(' ', idx - 1) + 1;
    let wordEnd = lowerText.indexOf(' ', idx + query.length);
    if (wordEnd === -1) wordEnd = text.length;
    const beforeWord = text.slice(0, wordStart);
    const word = text.slice(wordStart, wordEnd);
    const afterWord = text.slice(wordEnd);
    const mIdx = idx - wordStart;
    return (
      <>
        {beforeWord && <span style={{ color: COLORS.text }}>{beforeWord}</span>}
        <span style={{ color: COLORS.green }}>
          {word.slice(0, mIdx)}
          <strong>{word.slice(mIdx, mIdx + query.length)}</strong>
          {word.slice(mIdx + query.length)}
        </span>
        {afterWord && <span style={{ color: COLORS.text }}>{afterWord}</span>}
      </>
    );
  };

  const cargarRecetas = async () => {
    try {
      const data = await fetch(API + "/recetas", { credentials: "include" }).then(r => r.json());
      setRecetas(data || []);
    } catch {}
  };

  const borrarReceta = async (id) => {
    await fetch(API + `/recetas/${id}`, { method: "DELETE", credentials: "include" });
    cargarRecetas();
    setVerReceta(null);
    showToast("Receta eliminada");
  };

  useEffect(() => { if (!creating) cargarRecetas(); }, [creating]);

  const resetCreating = () => {
    setCreating(false); setNombre(""); setIngredientes([]); setPaso("input");
    setIngQuery(""); setIngResults([]);
    setNumPorciones("1"); setTipoRegistro("porciones"); setPreparacion(""); setMetadataExtra({}); setPanelPrep(false);
    setShowWizardManual(false); setMissingLabelIdx(null);
  };

  // Procesar resultado de scan (tras recibir respuesta de /scan-receta)
  const procesarScanResult = async (scan) => {
    setPaso("analizando");
    try {
      let rawIngs = [];
      if (scan.ingredientes?.length) {
        rawIngs = scan.ingredientes;
      } else if (scan.texto) {
        const resp1 = await fetch(API + "/recetas/analizar", {
          method: "POST", headers: {"Content-Type": "application/json"}, credentials: "include",
          body: JSON.stringify({nombre: scan.nombre || nombre, texto_receta: scan.texto})
        }).then(r => r.json());
        rawIngs = resp1.ingredientes || [];
      }
      if (rawIngs.length === 0) { showToast("No se encontraron ingredientes"); setPaso("input"); return; }
      if (scan.nombre && !nombre) setNombre(scan.nombre);
      if (scan.preparacion) { setPreparacion(p => p || scan.preparacion); setPanelPrep(true); }
      const EXTRA_KEYS = ["tiempo", "fuente", "notas", "informacion_nutricional", "beneficios"];
      const extra = {};
      EXTRA_KEYS.forEach(k => { if (scan[k]) extra[k] = scan[k]; });
      if (Object.keys(extra).length) setMetadataExtra(p => ({...p, ...extra}));
      const resolved = await Promise.all(rawIngs.map(async (ing) => {
        const results = await fetch(API + `/alimentos/local?q=${encodeURIComponent(ing.nombre)}`, {credentials: "include"}).then(r => r.json()).catch(() => []);
        const found = Array.isArray(results) && results.length > 0 ? results[0] : null;
        if (found && (found.calorias > 0 || found.proteina > 0)) {
          const tipoMedida = found.tipo_medida || "peso";
          return {
            nombre: found.nombre, gramos: ing.gramos || 100, cantidad: ing.gramos || 100,
            tipo_medida: tipoMedida, peso_unidad: found.peso_unidad || null,
            densidad: found.densidad || 1.0, nombre_unidad: found.nombre_unidad || null,
            calorias_100g: found.calorias, proteina_100g: found.proteina,
            carbos_100g: found.carbos, grasa_100g: found.grasa, fuente: "local", missing: false,
          };
        } else {
          return {
            nombre: ing.nombre, gramos: ing.gramos || 100, cantidad: ing.gramos || 100,
            tipo_medida: "peso", peso_unidad: null, densidad: 1.0, nombre_unidad: null,
            calorias_100g: 0, proteina_100g: 0, carbos_100g: 0, grasa_100g: 0,
            fuente: "scan", missing: true,
          };
        }
      }));
      setIngredientes(p => [...p, ...resolved]);
    } catch { showToast("Error al procesar"); }
    setPaso("input");
  };

  // Buscar ingrediente (crear)
  const searchIng = async (query) => {
    if (query.length < 2) { setIngResults([]); return; }
    const [local, base] = await Promise.all([
      fetch(API + `/alimentos/buscar?q=${encodeURIComponent(query)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      fetch(API + `/alimentos/local?q=${encodeURIComponent(query)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
    ]);
    setIngResults([...(local||[]), ...(base||[])].filter((v,i,a) => a.findIndex(t => t.nombre === v.nombre) === i).slice(0, 5));
  };

  // Añadir ingrediente desde búsqueda
  const addIng = (s) => {
    // Enfocar input oculto síncronamente dentro del gesto → iOS abre el teclado
    hiddenNumRef.current?.focus();
    const tipoMedida = s.tipo_medida || "peso";
    setIngredientes(p => [...p, {
      nombre: s.nombre,
      gramos: 0,
      cantidad: 0,
      tipo_medida: tipoMedida,
      peso_unidad: s.peso_unidad || null,
      densidad: s.densidad || 1.0,
      nombre_unidad: s.nombre_unidad || null,
      calorias_100g: s.calorias,
      proteina_100g: s.proteina,
      carbos_100g: s.carbos,
      grasa_100g: s.grasa,
      fuente: "local",
    }]);
    setIngQuery(""); setIngResults([]);
    setShouldFocusCantidad(true);
  };

  // Wizard nuevo ingrediente desde receta
  const handleWizardComplete = async (data) => {
    await fetch(API + "/alimentos", {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({
        nombre: data.nombre, calorias: data.calorias, proteina: data.proteina,
        carbos: data.carbos, grasa: data.grasa, fuente: "manual",
        tipo_medida: data.tipo_medida, tipo_cantidad: data.tipo_cantidad,
        peso_unidad: data.peso_unidad, densidad: data.densidad, nombre_unidad: data.nombre_unidad,
      }),
    }).catch(() => {});
    const tipoMedida = data.tipo_medida || "peso";
    const cantidadInicial = parseFloat(data.cantidad) || 0;
    let gramosInicial = 0;
    if (tipoMedida === "unidad") gramosInicial = cantidadInicial * (data.peso_unidad || 100);
    else if (tipoMedida === "liquido") gramosInicial = cantidadInicial * (data.densidad || 1.0);
    else gramosInicial = cantidadInicial;
    setIngredientes(p => [...p, {
      nombre: data.nombre,
      gramos: gramosInicial,
      cantidad: cantidadInicial,
      tipo_medida: tipoMedida,
      peso_unidad: data.peso_unidad || null,
      densidad: data.densidad || 1.0,
      nombre_unidad: data.nombre_unidad || null,
      calorias_100g: data.calorias,
      proteina_100g: data.proteina,
      carbos_100g: data.carbos,
      grasa_100g: data.grasa,
      fuente: "local",
    }]);
    setShowWizardManual(false);
    showToast("Ingrediente añadido a la BD y a la receta");
  };


  // Guardar receta
  const guardar = async () => {
    if (!nombre || ingredientes.length === 0) return;
    setSaving(true);
    setPaso("guardando");
    const peso = ingredientes.reduce((s, i) => s + (i.gramos || 0), 0);
    const cal = ingredientes.reduce((s, i) => s + i.calorias_100g * i.gramos / 100, 0);
    const prot = ingredientes.reduce((s, i) => s + i.proteina_100g * i.gramos / 100, 0);
    const carb = ingredientes.reduce((s, i) => s + i.carbos_100g * i.gramos / 100, 0);
    const gras = ingredientes.reduce((s, i) => s + i.grasa_100g * i.gramos / 100, 0);
    const macros = { peso_total: peso, calorias_100g: peso > 0 ? Math.round(cal*100/peso*10)/10 : 0, proteina_100g: peso > 0 ? Math.round(prot*100/peso*10)/10 : 0, carbos_100g: peso > 0 ? Math.round(carb*100/peso*10)/10 : 0, grasa_100g: peso > 0 ? Math.round(gras*100/peso*10)/10 : 0 };
    const porciones = Math.max(1, parseInt(numPorciones) || 1);
    await fetch(API + "/recetas", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ nombre, ingredientes: sortIngs(ingredientes), ...macros, num_porciones: porciones, tipo_registro: tipoRegistro, preparacion: preparacion.trim(), extra_info: Object.keys(metadataExtra).length ? JSON.stringify(metadataExtra) : undefined }) });
    await fetch(API + "/alimentos", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ nombre, calorias: macros.calorias_100g, proteina: macros.proteina_100g, carbos: macros.carbos_100g, grasa: macros.grasa_100g, tipo_cantidad: tipoRegistro === "porciones" && porciones > 1 ? "porcion" : "peso", peso_unidad: tipoRegistro === "porciones" && porciones > 1 ? Math.round(peso / porciones * 10) / 10 : null }) }).catch(() => {});
    setSaving(false);
    resetCreating();
    showToast("✅ Receta guardada");
  };

  // Editar receta
  const searchEditIng = async (query) => {
    if (query.length < 2) { setEditIngResults([]); return; }
    const [local, base] = await Promise.all([
      fetch(API + `/alimentos/buscar?q=${encodeURIComponent(query)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      fetch(API + `/alimentos/local?q=${encodeURIComponent(query)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
    ]);
    setEditIngResults([...(local||[]), ...(base||[])].filter((v,i,a) => a.findIndex(t => t.nombre === v.nombre) === i).slice(0, 5));
  };
  const addEditIng = (s) => {
    setEditandoReceta(r => ({ ...r, ingredientes: [...r.ingredientes, { nombre: s.nombre, gramos: 0, tipo_medida: s.tipo_medida || 'peso', peso_unidad: s.peso_unidad || null, densidad: s.densidad || 1.0, calorias_100g: s.calorias, proteina_100g: s.proteina, carbos_100g: s.carbos, grasa_100g: s.grasa, fuente: "local" }]}));
    setEditIngQuery(""); setEditIngResults([]);
  };
  const addEditIngManual = () => {
    if (!editIngQuery.trim()) return;
    setEditandoReceta(r => ({ ...r, ingredientes: [...r.ingredientes, { nombre: editIngQuery.trim(), gramos: 0, calorias_100g: 0, proteina_100g: 0, carbos_100g: 0, grasa_100g: 0, fuente: "local" }]}));
    setEditIngQuery(""); setEditIngResults([]);
  };

  // Helpers unidad
  const getUnitLabel = (ing) => {
    const tm = ing.tipo_medida || "peso";
    if (tm === "unidad") return "uds";
    if (tm === "liquido") return "ml";
    return "g";
  };
  const getCantidad = (ing) => ing.cantidad !== undefined ? ing.cantidad : ing.gramos;

  // Handler cantidad adaptativa
  const handleCantidadChange = (rawVal, idx) => {
    const v = rawVal === "" ? 0 : Number(rawVal.replace(/^0+(\d)/, "$1"));
    setIngredientes(p => {
      const n = [...p];
      const tm = n[idx].tipo_medida || "peso";
      let gramos;
      if (tm === "unidad") gramos = v * (n[idx].peso_unidad || 100);
      else if (tm === "liquido") gramos = v * (n[idx].densidad || 1.0);
      else gramos = v;
      n[idx] = { ...n[idx], cantidad: v, gramos };
      return n;
    });
  };

  const recetasFiltradas = recetas.filter(r =>
    !busquedaRecetas || r.nombre.toLowerCase().includes(busquedaRecetas.toLowerCase())
  );

  return (
    <div style={{ padding: "0 16px 100px" }}>
      {toast && <div style={{ position: "fixed", top: 60, left: "50%", transform: "translateX(-50%)", background: COLORS.green, color: "#fff", padding: "10px 24px", borderRadius: 20, fontWeight: 700, fontSize: 14, zIndex: 999 }}>{toast}</div>}

      {/* Header */}
      <div style={{ paddingTop: 'calc(var(--sat, env(safe-area-inset-top, 0px)) + 14px)', marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 style={{ color: "#fff", fontSize: 28, fontWeight: 900, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -1, lineHeight: 1.1 }}>Recetas</h1>
          <input
            value={busquedaRecetas}
            onChange={e => setBusquedaRecetas(e.target.value)}
            placeholder="Buscar..."
            style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "7px 14px", color: COLORS.text, fontSize: 13, outline: "none", width: 140 }}
          />
        </div>
      </div>


      <button onClick={() => setCreating(true)} style={{ width: "100%", padding: 16, borderRadius: 14, marginBottom: 16, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: `0 8px 24px ${COLORS.accentGlow}` }}>+ NUEVA RECETA</button>

      {/* Lista de recetas */}
      {recetasFiltradas.length === 0 && recetas.length === 0 && (
        <p style={{ color: COLORS.textDim, fontSize: 13, textAlign: "center", marginTop: 40 }}>Aún no tienes recetas guardadas</p>
      )}
      {recetasFiltradas.map((r, i) => (
        <SwipeableRow key={r.id || i} onDelete={() => borrarReceta(r.id)}>
          <div onClick={() => setVerReceta(r)} style={{ background: COLORS.card, borderRadius: 14, padding: 16, border: `1px solid ${COLORS.border}`, cursor: "pointer", marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{r.nombre}</span>
              <span style={{ color: COLORS.textDim, fontSize: 11 }}>{r1(r.peso_total)}g ›</span>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              {(() => {
                const esPorciones = r.tipo_registro === "porciones" && r.num_porciones > 0 && r.peso_total;
                const factor = esPorciones ? r.peso_total / r.num_porciones / 100 : 1;
                const lbl = esPorciones ? (r.num_porciones === 1 ? "total" : "por ración") : "/100g";
                return [["kcal", r1(r.calorias_100g * factor), COLORS.accent], ["prot", r1(r.proteina_100g * factor), COLORS.blue], ["carb", r1(r.carbos_100g * factor), COLORS.green], ["grasa", r1(r.grasa_100g * factor), COLORS.purple]].map(([l, v, c]) => (
                  <div key={l} style={{ textAlign: "center" }}>
                    <div style={{ color: c, fontWeight: 800, fontSize: 14 }}>{v}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 10 }}>{l} {lbl}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </SwipeableRow>
      ))}

      {/* Modal detalle receta */}
      {verReceta && (
        <Popup type="sheet" onClose={() => { setVerReceta(null); setExpandedMacroRow(null); }} zIndex={500}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ color: COLORS.text, fontSize: 20, fontWeight: 900 }}>{verReceta.nombre}</span>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button onClick={() => {
                  setEditandoReceta({ ...verReceta, ingredientes: verReceta.ingredientes ? [...verReceta.ingredientes] : [] });
                  setVerReceta(null);
                }} style={{ background: "none", border: "none", color: COLORS.blue, fontSize: 18, cursor: "pointer" }}>✎</button>
                <button onClick={() => borrarReceta(verReceta.id)} style={{ background: "none", border: "none", color: "#ff2d55", fontSize: 18, cursor: "pointer" }}>🗑</button>
                <button onClick={() => setVerReceta(null)} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 24, cursor: "pointer" }}>✕</button>
              </div>
            </div>
            <div style={{ background: COLORS.bg, borderRadius: 12, padding: 14, marginBottom: 20 }}>
              {(() => {
                const esPorciones = (verReceta.tipo_registro || "porciones") === "porciones";
                const nPorciones = verReceta.num_porciones || 1;
                const totalCal = verReceta.calorias_100g * verReceta.peso_total / 100;
                const totalProt = verReceta.proteina_100g * verReceta.peso_total / 100;
                const totalCarb = verReceta.carbos_100g * verReceta.peso_total / 100;
                const totalGras = verReceta.grasa_100g * verReceta.peso_total / 100;
                const porCal = r1(totalCal / nPorciones);
                const porProt = r1(totalProt / nPorciones);
                const porCarb = r1(totalCarb / nPorciones);
                const porGras = r1(totalGras / nPorciones);
                const ings = verReceta.ingredientes || [];
                const pesoT = verReceta.peso_total || 1;
                const MacroBreakdown = ({ mode }) => (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
                    {sortIngs(ings).map((ing, i) => {
                      let cal, prot, carb, gras;
                      if (mode === 'totales') {
                        cal = r1(ing.calorias_100g * ing.gramos / 100 / nPorciones);
                        prot = r1(ing.proteina_100g * ing.gramos / 100 / nPorciones);
                        carb = r1(ing.carbos_100g * ing.gramos / 100 / nPorciones);
                        gras = r1(ing.grasa_100g * ing.gramos / 100 / nPorciones);
                      } else {
                        cal = r1((ing.gramos / pesoT) * ing.calorias_100g);
                        prot = r1((ing.gramos / pesoT) * ing.proteina_100g);
                        carb = r1((ing.gramos / pesoT) * ing.carbos_100g);
                        gras = r1((ing.gramos / pesoT) * ing.grasa_100g);
                      }
                      const gramsPor = mode === 'totales' ? r1(ing.gramos / nPorciones) : r1((ing.gramos / pesoT) * 100);
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < ings.length - 1 ? `1px solid ${COLORS.border}20` : 'none' }}>
                          <span style={{ color: COLORS.text, fontSize: 12, flex: 1 }}>{ing.nombre} <span style={{ color: COLORS.textMuted, fontSize: 11 }}>({gramsPor}g)</span></span>
                          <span style={{ color: COLORS.textMuted, fontSize: 11, textAlign: 'right' }}>{cal}kcal · {prot}p · {carb}c · {gras}g</span>
                        </div>
                      );
                    })}
                  </div>
                );
                return (
                  <>
                    {esPorciones && (
                      <>
                        <div
                          onClick={() => setExpandedMacroRow(p => p === 'totales' ? null : 'totales')}
                          style={{ cursor: 'pointer' }}
                        >
                          <p style={{ color: COLORS.textMuted, fontSize: 11, margin: "0 0 8px", letterSpacing: 1, display: 'flex', justifyContent: 'space-between' }}>
                            <span>{nPorciones === 1 ? "MACROS TOTALES" : `MACROS POR PORCIÓN (${r1(verReceta.peso_total / nPorciones)}G) · ${nPorciones} PORCIONES`}</span>
                            <span style={{ fontSize: 10 }}>{expandedMacroRow === 'totales' ? '▲' : '▼'}</span>
                          </p>
                          <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                            {[["Kcal", porCal, COLORS.accent], ["Proteína", porProt, COLORS.blue], ["Carbos", porCarb, COLORS.green], ["Grasa", porGras, COLORS.purple]].map(([l, v, c]) => (
                              <div key={l} style={{ flex: 1, textAlign: "center", background: COLORS.card, borderRadius: 10, padding: "10px 4px" }}>
                                <div style={{ color: c, fontWeight: 800, fontSize: 18 }}>{v}</div>
                                <div style={{ color: COLORS.textMuted, fontSize: 10 }}>{l}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {expandedMacroRow === 'totales' && <MacroBreakdown mode="totales" />}
                        <div style={{ marginBottom: 10 }} />
                      </>
                    )}
                    <div
                      onClick={() => setExpandedMacroRow(p => p === 'per100g' ? null : 'per100g')}
                      style={{ cursor: 'pointer' }}
                    >
                      <p style={{ color: COLORS.textMuted, fontSize: 11, margin: "0 0 8px", letterSpacing: 1, display: 'flex', justifyContent: 'space-between' }}>
                        <span>MACROS POR 100G · PESO TOTAL {r1(verReceta.peso_total)}g</span>
                        <span style={{ fontSize: 10 }}>{expandedMacroRow === 'per100g' ? '▲' : '▼'}</span>
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        {[["Kcal", r1(verReceta.calorias_100g)], ["Proteína", r1(verReceta.proteina_100g)], ["Carbos", r1(verReceta.carbos_100g)], ["Grasa", r1(verReceta.grasa_100g)]].map(([l, v]) => (
                          <div key={l} style={{ flex: 1, textAlign: "center", background: COLORS.card, borderRadius: 10, padding: "8px 4px" }}>
                            <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{v}</div>
                            <div style={{ color: COLORS.textMuted, fontSize: 10 }}>{l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {expandedMacroRow === 'per100g' && <MacroBreakdown mode="per100g" />}
                  </>
                );
              })()}
            </div>
            <p style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 1, margin: "0 0 10px" }}>INGREDIENTES</p>
            {verReceta.ingredientes && sortIngs(verReceta.ingredientes).map((ing, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                <span style={{ color: COLORS.text, fontSize: 14 }}>{ing.nombre}</span>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ color: COLORS.textMuted, fontSize: 13 }}>{ing.gramos}g</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{r1(ing.calorias_100g * ing.gramos / 100)} kcal</span>
                  <span style={{ color: ing.fuente === "local" ? COLORS.green : COLORS.blue, fontSize: 10, fontWeight: 700 }}>{ing.fuente === "local" ? "LOCAL" : "API"}</span>
                </div>
              </div>
            ))}

            {verReceta.preparacion && (
              <div style={{ marginTop: 20 }}>
                <p style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 1, margin: "0 0 10px" }}>PREPARACIÓN</p>
                <p style={{ color: COLORS.text, fontSize: 14, lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{verReceta.preparacion}</p>
              </div>
            )}

            {(() => {
              const LABELS = { tiempo: "TIEMPO", fuente: "RECETA DE", notas: "NOTAS", informacion_nutricional: "INFORMACIÓN NUTRICIONAL", beneficios: "BENEFICIOS" };
              let meta = {};
              try { if (verReceta.extra_info) meta = JSON.parse(verReceta.extra_info); } catch {}
              return Object.entries(meta).map(([k, v]) => v ? (
                <div key={k} style={{ marginTop: 16 }}>
                  <p style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 1, margin: "0 0 8px" }}>{LABELS[k] || k.toUpperCase().replace(/_/g, " ")}</p>
                  <p style={{ color: COLORS.text, fontSize: 14, lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{v}</p>
                </div>
              ) : null);
            })()}

            <button
              onClick={() => { setPrepEditText(verReceta.preparacion || ""); setEditandoPreparacion(verReceta); }}
              style={{ width: "100%", marginTop: 24, padding: 14, borderRadius: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              📝 {verReceta.preparacion ? "Modificar preparación" : "Añadir preparación"}
            </button>
        </Popup>
      )}

      {/* Modal editar preparación */}
      {editandoPreparacion && (
        <Popup onClose={() => setEditandoPreparacion(null)} zIndex={600}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: COLORS.text, fontSize: 16, fontWeight: 800 }}>Preparación</span>
            <button onClick={() => setEditandoPreparacion(null)} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 24, cursor: "pointer" }}>✕</button>
          </div>
          <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "0 0 12px" }}>{editandoPreparacion.nombre}</p>
          <ImagePicker
            endpoint="/recetas/scan-preparacion"
            title="📷 Fotos de preparación"
            description="Fotografía los pasos, notas u origen de la receta."
            onLoading={(l) => { if (l) setPrepScanLoading(true); else setPrepScanLoading(false); }}
            onResult={(res) => { if (res.texto) setPrepEditText(p => p ? p + "\n\n" + res.texto : res.texto); setPrepScanLoading(false); }}
            onError={() => { showToast("Error al escanear preparación"); setPrepScanLoading(false); }}
          >
            {(triggerOpen) => (
              <button onClick={triggerOpen} disabled={prepScanLoading} style={{ width: "100%", padding: "9px 12px", borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.blue}`, color: COLORS.blue, fontSize: 12, fontWeight: 700, cursor: "pointer", marginBottom: 12 }}>
                {prepScanLoading ? "⏳ Analizando..." : "📷 Escanear desde fotos"}
              </button>
            )}
          </ImagePicker>
          <textarea
            value={prepEditText}
            onChange={e => setPrepEditText(e.target.value)}
            placeholder="Pasos de preparación, notas, origen de la receta, quién te la dio..."
            autoFocus={false}
            style={{ width: "100%", minHeight: 160, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.5, marginBottom: 14 }}
          />
          <button onClick={async () => {
            await fetch(API + `/recetas/${editandoPreparacion.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ ...editandoPreparacion, preparacion: prepEditText.trim() }) });
            const updated = await fetch(API + "/recetas", { credentials: "include" }).then(r => r.json());
            setRecetas(updated);
            const recetaActualizada = updated.find(r => r.id === editandoPreparacion.id);
            if (recetaActualizada) setVerReceta(recetaActualizada);
            setEditandoPreparacion(null);
            showToast("✅ Preparación guardada");
          }} style={{ width: "100%", padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: "none", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
            GUARDAR PREPARACIÓN
          </button>
        </Popup>
      )}


      {/* Modal editar receta */}
      {editandoReceta && (
        <MaximizedPopup>
          <div style={{ padding: "24px 20px 44px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 800 }}>Editar receta</span>
              <button onClick={() => { setEditandoReceta(null); setEditIngQuery(""); setEditIngResults([]); }} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 24, cursor: "pointer" }}>✕</button>
            </div>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>NOMBRE</label>
            <input value={editandoReceta.nombre} onChange={e => setEditandoReceta(r => ({ ...r, nombre: e.target.value }))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 15, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>PORCIONES</label>
                <input type="number" min="1" value={editandoReceta.num_porciones ?? ""} onChange={e => setEditandoReceta(r => ({ ...r, num_porciones: e.target.value.replace(/[^0-9]/g, '') }))} disabled={editandoReceta.tipo_registro === "gramos"} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: editandoReceta.tipo_registro === "gramos" ? COLORS.textDim : COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box", opacity: editandoReceta.tipo_registro === "gramos" ? 0.4 : 1 }} />
              </div>
              <div style={{ flexShrink: 0, paddingTop: 18 }}>
                <button onClick={() => setEditandoReceta(r => ({ ...r, tipo_registro: r.tipo_registro === "gramos" ? "porciones" : "gramos" }))} style={{ padding: "10px 14px", borderRadius: 10, border: `1px solid ${editandoReceta.tipo_registro === "gramos" ? COLORS.accent : COLORS.border}`, background: editandoReceta.tipo_registro === "gramos" ? COLORS.accentSoft : COLORS.bg, color: editandoReceta.tipo_registro === "gramos" ? COLORS.accent : COLORS.textMuted, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Gr</button>
              </div>
            </div>
            <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 8 }}>INGREDIENTES</label>
            {editandoReceta.ingredientes.map((ing, i) => (
              <div key={i}>
                <div style={{ display: "flex", gap: 8, marginBottom: (editIngEditIdx === i && (editIngEditResults.length > 0 || editIngEditQuery.length >= 2)) ? 0 : 8, alignItems: "center" }}>
                  <input value={ing.nombre} onChange={e => {
                    const n = e.target.value;
                    setEditandoReceta(r => { const ings = [...r.ingredientes]; ings[i] = { ...ings[i], nombre: n }; return { ...r, ingredientes: ings }; });
                    setEditIngEditIdx(i); setEditIngEditQuery(n);
                    clearTimeout(editIngEditTimeout.current);
                    if (n.length >= 2) {
                      editIngEditTimeout.current = setTimeout(async () => {
                        const [local, base] = await Promise.all([
                          fetch(API + `/alimentos/buscar?q=${encodeURIComponent(n)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
                          fetch(API + `/alimentos/local?q=${encodeURIComponent(n)}`, { credentials: "include" }).then(r => r.json()).catch(() => []),
                        ]);
                        setEditIngEditResults([...(local||[]), ...(base||[])].filter((v,j,a) => a.findIndex(t => t.nombre === v.nombre) === j).slice(0, 5));
                      }, 350);
                    } else { setEditIngEditResults([]); }
                  }} style={{ flex: 2, minWidth: 0, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 10px", color: COLORS.text, fontSize: 13, outline: "none" }} />
                  <input value={ing.gramos || ""} type="number" onChange={e => { const v = e.target.value === "" ? 0 : Number(e.target.value.replace(/^0+(\d)/, "$1")); setEditandoReceta(r => { const ings = [...r.ingredientes]; ings[i] = { ...ings[i], gramos: v }; return { ...r, ingredientes: ings }; }); }} style={{ flex: 1, minWidth: 0, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 10px", color: COLORS.text, fontSize: 13, outline: "none" }} placeholder={getUnitLabel(ing)} />
                  <button onClick={() => setEditandoReceta(r => ({ ...r, ingredientes: r.ingredientes.filter((_, j) => j !== i) }))} style={{ flexShrink: 0, background: "none", border: "none", color: "#ff2d55", fontSize: 18, cursor: "pointer", padding: "4px 6px" }}>✕</button>
                </div>
                {editIngEditIdx === i && (editIngEditResults.length > 0 || editIngEditQuery.length >= 2) && (
                  <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 8 }}>
                    {editIngEditResults.map((s, j) => (
                      <div key={j} onClick={() => {
                        setEditandoReceta(r => { const ings = [...r.ingredientes]; ings[i] = { ...ings[i], nombre: s.nombre, tipo_medida: s.tipo_medida || 'peso', peso_unidad: s.peso_unidad || null, densidad: s.densidad || 1.0, calorias_100g: s.calorias, proteina_100g: s.proteina, carbos_100g: s.carbos, grasa_100g: s.grasa }; return { ...r, ingredientes: ings }; });
                        setEditIngEditIdx(null); setEditIngEditQuery(""); setEditIngEditResults([]);
                      }} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${COLORS.border}` }}>
                        <span style={{ fontSize: 13 }}>{highlightMatch(s.nombre, editIngEditQuery)}</span>
                        <span style={{ color: COLORS.textMuted, fontSize: 11, marginLeft: 8 }}>{r1(s.calorias)} kcal · {r1(s.proteina)}g prot</span>
                      </div>
                    ))}
                    {editIngEditQuery.length >= 2 && (
                      <div onClick={async () => {
                        try {
                          const res = await fetch(API + `/alimentos/online?q=${encodeURIComponent(editIngEditQuery)}`, { credentials: "include" }).then(r => r.json());
                          const first = Array.isArray(res) && res.length > 0 ? res[0] : null;
                          if (first && first.calorias) {
                            setEditScanConfirm({ nombre: first.nombre || editIngEditQuery, calorias: first.calorias, proteina: first.proteina || 0, carbos: first.carbos || 0, grasa: first.grasa || 0, fuente: "online", idx: i });
                          } else {
                            const claude = await fetch(API + "/alimentos/buscar-nombre", { method: "POST", headers: {"Content-Type": "application/json"}, credentials: "include", body: JSON.stringify({ nombre: editIngEditQuery }) }).then(r => r.json()).catch(() => null);
                            if (claude && claude.calorias !== undefined) setEditScanConfirm({ nombre: claude.nombre || editIngEditQuery, calorias: claude.calorias, proteina: claude.proteina || 0, carbos: claude.carbos || 0, grasa: claude.grasa || 0, fuente: "api", idx: i });
                            else showToast("No se encontraron datos");
                          }
                        } catch { showToast("Error al buscar en internet"); }
                      }} style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: COLORS.blue, fontSize: 13, fontWeight: 700 }}>
                        <span>🌐</span><span>Buscar "{editIngEditQuery}" en internet</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div style={{ position: "relative", marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={editIngQuery}
                  onChange={e => { setEditIngQuery(e.target.value); clearTimeout(editIngTimeout.current); editIngTimeout.current = setTimeout(() => searchEditIng(e.target.value), 350); }}
                  placeholder="Buscar ingrediente..."
                  style={{ flex: 1, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "9px 12px", color: COLORS.text, fontSize: 13, outline: "none" }}
                />
                <button onClick={addEditIngManual} disabled={!editIngQuery.trim()} style={{ padding: "9px 14px", borderRadius: 10, background: editIngQuery.trim() ? COLORS.accentSoft : COLORS.bg, border: `1px solid ${COLORS.border}`, color: editIngQuery.trim() ? COLORS.accent : COLORS.textDim, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>+ Manual</button>
              </div>
              {(editIngResults.length > 0 || editIngQuery.length >= 2) && (
                <div ref={editSuggestionsRef} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden", marginTop: 4 }}>
                  {editIngResults.map((s, i) => (
                    <div key={i} onClick={() => addEditIng(s)} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${COLORS.border}` }}>
                      <span style={{ fontSize: 13 }}>{highlightMatch(s.nombre, editIngQuery)}</span>
                      <span style={{ color: COLORS.textMuted, fontSize: 11, marginLeft: 8 }}>{r1(s.calorias)} kcal · {r1(s.proteina)}g prot</span>
                    </div>
                  ))}
                  {editIngQuery.length >= 2 && (
                    <div
                      onClick={async () => {
                        try {
                          const res = await fetch(API + `/alimentos/online?q=${encodeURIComponent(editIngQuery)}`, { credentials: "include" }).then(r => r.json());
                          const first = Array.isArray(res) && res.length > 0 ? res[0] : null;
                          if (first && first.calorias) {
                            setEditScanConfirm({ nombre: first.nombre || editIngQuery, calorias: first.calorias, proteina: first.proteina || 0, carbos: first.carbos || 0, grasa: first.grasa || 0, fuente: "online" });
                          } else {
                            const claude = await fetch(API + "/alimentos/buscar-nombre", { method: "POST", headers: {"Content-Type": "application/json"}, credentials: "include", body: JSON.stringify({ nombre: editIngQuery }) }).then(r => r.json()).catch(() => null);
                            if (claude && claude.calorias !== undefined) setEditScanConfirm({ nombre: claude.nombre || editIngQuery, calorias: claude.calorias, proteina: claude.proteina || 0, carbos: claude.carbos || 0, grasa: claude.grasa || 0, fuente: "api" });
                            else showToast("No se encontraron datos");
                          }
                        } catch { showToast("Error al buscar en internet"); }
                      }}
                      style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: COLORS.blue, fontSize: 13, fontWeight: 700 }}
                    >
                      <span>🌐</span><span>Buscar "{editIngQuery}" en internet</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button disabled={saving} onClick={async () => {
              setSaving(true);
              const peso = editandoReceta.ingredientes.reduce((s, i) => s + (i.gramos || 0), 0);
              const cal = editandoReceta.ingredientes.reduce((s, i) => s + i.calorias_100g * i.gramos / 100, 0);
              const prot = editandoReceta.ingredientes.reduce((s, i) => s + i.proteina_100g * i.gramos / 100, 0);
              const carb = editandoReceta.ingredientes.reduce((s, i) => s + i.carbos_100g * i.gramos / 100, 0);
              const gras = editandoReceta.ingredientes.reduce((s, i) => s + i.grasa_100g * i.gramos / 100, 0);
              const updated = { ...editandoReceta, ingredientes: sortIngs(editandoReceta.ingredientes), num_porciones: Math.max(1, parseInt(editandoReceta.num_porciones) || 1), tipo_registro: editandoReceta.tipo_registro || "porciones", peso_total: peso, calorias_100g: peso > 0 ? Math.round(cal * 100 / peso * 10) / 10 : 0, proteina_100g: peso > 0 ? Math.round(prot * 100 / peso * 10) / 10 : 0, carbos_100g: peso > 0 ? Math.round(carb * 100 / peso * 10) / 10 : 0, grasa_100g: peso > 0 ? Math.round(gras * 100 / peso * 10) / 10 : 0 };
              try {
                await fetch(API + `/recetas/${editandoReceta.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(updated) });
                await fetch(API + "/alimentos", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ nombre: updated.nombre, calorias: updated.calorias_100g, proteina: updated.proteina_100g, carbos: updated.carbos_100g, grasa: updated.grasa_100g, tipo_cantidad: updated.tipo_registro === "porciones" && updated.num_porciones > 1 ? "porcion" : "peso", peso_unidad: updated.tipo_registro === "porciones" && updated.num_porciones > 1 ? Math.round(peso / updated.num_porciones * 10) / 10 : null }) }).catch(() => {});
                cargarRecetas();
                setEditandoReceta(null);
              } finally { setSaving(false); }
            }} style={{ width: "100%", padding: 16, borderRadius: 14, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: saving ? "default" : "pointer" }}>{saving ? "⏳" : "GUARDAR CAMBIOS"}</button>
          </div>
        </MaximizedPopup>
      )}

      {/* ══ MODAL CREAR RECETA ══ */}
      {creating && (
        <MaximizedPopup onBackdropClick={() => { if (paso === "input") resetCreating(); }}>
          {/* Input oculto para desbloquear teclado iOS en gesto de usuario */}
          <input ref={hiddenNumRef} type="number" inputMode="decimal" readOnly style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} />
          <div style={{ display: "flex", flexDirection: "column" }}>

            {/* Header */}
            <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontSize: 22, fontWeight: 900, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: -0.5 }}>Crear receta</span>
            </div>

            {/* Zona scrollable */}
            <div style={{ padding: "20px 20px 24px" }}>

              {paso === "input" && (
                <>
                  {/* Nombre + botón cerrar */}
                  <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 2, display: "block", marginBottom: 6, fontWeight: 800 }}>NOMBRE DE LA RECETA</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
                    <input
                      value={nombre}
                      onChange={e => setNombre(e.target.value)}
                      placeholder="Ej: Tortilla de patata"
                      style={{ flex: 1, minWidth: 0, background: '#1a1a26', border: '1px solid #2a2a3a', borderRadius: 12, padding: "11px 14px", color: COLORS.text, fontSize: 15, fontWeight: 600, outline: "none", boxSizing: "border-box", transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
                    />
                    <button onClick={resetCreating} style={{ flexShrink: 0, background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, width: 42, height: 42, color: COLORS.textMuted, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                  </div>

                  {/* Porciones + [Gr] toggle + Guardar en la misma fila */}
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      <span style={{ color: tipoRegistro === "gramos" ? COLORS.textDim : COLORS.textMuted, fontSize: 12, fontWeight: 700 }}>Porciones</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        value={numPorciones}
                        onChange={e => setNumPorciones(e.target.value.replace(/[^0-9]/g, ''))}
                        disabled={tipoRegistro === "gramos"}
                        style={{ width: 52, background: tipoRegistro === "gramos" ? 'rgba(255,255,255,0.03)' : '#1a1a26', border: '1px solid #2a2a3a', borderRadius: 12, padding: "8px 10px", color: tipoRegistro === "gramos" ? COLORS.textDim : COLORS.accent, fontSize: 15, fontWeight: 800, outline: "none", textAlign: "center", opacity: tipoRegistro === "gramos" ? 0.4 : 1 }}
                      />
                      <button
                        onClick={() => setTipoRegistro(t => t === "gramos" ? "porciones" : "gramos")}
                        style={{ padding: "6px 12px", borderRadius: 10, border: `1px solid ${tipoRegistro === "gramos" ? COLORS.accent : 'rgba(255,255,255,0.15)'}`, background: tipoRegistro === "gramos" ? COLORS.accentSoft : 'rgba(255,255,255,0.05)', color: tipoRegistro === "gramos" ? COLORS.accent : COLORS.textMuted, fontSize: 12, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}
                      >Gr</button>
                    </div>
                    <button
                      onClick={guardar}
                      disabled={!nombre || ingredientes.length === 0 || saving}
                      style={{
                        flex: 1, padding: "11px 16px", borderRadius: 14,
                        background: (!nombre || ingredientes.length === 0) ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #ff6b35, #e55a2b)',
                        backdropFilter: (!nombre || ingredientes.length === 0) ? 'blur(12px)' : 'none',
                        WebkitBackdropFilter: (!nombre || ingredientes.length === 0) ? 'blur(12px)' : 'none',
                        border: (!nombre || ingredientes.length === 0) ? '1px solid rgba(255,255,255,0.12)' : 'none',
                        color: (!nombre || ingredientes.length === 0) ? COLORS.textDim : "#fff",
                        fontSize: 13, fontWeight: 800,
                        cursor: (!nombre || ingredientes.length === 0) ? "default" : "pointer",
                        letterSpacing: 0.5,
                        transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: (!nombre || ingredientes.length === 0) ? "none" : '0 8px 24px rgba(253,139,0,0.4)',
                      }}
                    >
                      GUARDAR RECETA
                    </button>
                  </div>

                  {/* Búsqueda ingrediente — siempre visible, botón pequeño a la izquierda */}
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                      <button
                        onClick={() => ingInputRef.current?.focus()}
                        style={{ flexShrink: 0, padding: "8px 12px", borderRadius: 14, background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)', color: COLORS.textMuted, fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        + Ingrediente
                      </button>
                      <input
                        ref={ingInputRef}
                        value={ingQuery}
                        onChange={e => { setIngQuery(e.target.value); clearTimeout(ingSearchTimeout.current); ingSearchTimeout.current = setTimeout(() => searchIng(e.target.value), 350); }}
                        placeholder="Busca un ingrediente..."
                        style={{
                          flex: 1, background: '#1a1a26',
                          border: `1px solid ${ingQuery.length > 0 ? COLORS.accent : '#2a2a3a'}`,
                          borderRadius: 12, padding: "8px 12px", color: COLORS.text, fontSize: 14,
                          outline: "none", boxSizing: "border-box",
                          transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    </div>
                    {(ingResults.length > 0 || (ingQuery.length >= 2 && ingResults.length === 0)) && (
                      <div ref={ingSuggestionsRef} style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, overflow: "hidden", marginBottom: 8 }}>
                        {ingResults.map((s, i) => (
                          <div
                            key={i}
                            onClick={() => addIng(s)}
                            style={{
                              padding: "11px 14px", cursor: "pointer",
                              borderBottom: '1px solid rgba(255,255,255,0.08)',
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}
                          >
                            <span style={{ fontSize: 14, color: COLORS.text }}>{highlightMatch(s.nombre, ingQuery)}</span>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                              <span style={{ color: COLORS.accent, fontSize: 12, fontWeight: 700 }}>{r1(s.calorias)} kcal</span>
                              <span style={{ color: COLORS.textDim, fontSize: 10 }}>{r1(s.proteina)}g prot · {s.tipo_medida === "unidad" ? "por ud." : s.tipo_medida === "liquido" ? "por 100ml" : "por 100g"}</span>
                            </div>
                          </div>
                        ))}
                        {ingQuery.length >= 2 && (
                          <div
                            onClick={async () => {
                              try {
                                const res = await fetch(API + `/alimentos/online?q=${encodeURIComponent(ingQuery)}`, { credentials: "include" }).then(r => r.json());
                                const first = Array.isArray(res) && res.length > 0 ? res[0] : null;
                                if (first && first.calorias) {
                                  setScanConfirmReceta({ nombre: first.nombre || ingQuery, calorias: first.calorias, proteina: first.proteina || 0, carbos: first.carbos || 0, grasa: first.grasa || 0, fuente: "online" });
                                } else {
                                  const claude = await fetch(API + "/alimentos/buscar-nombre", { method: "POST", headers: {"Content-Type": "application/json"}, credentials: "include", body: JSON.stringify({ nombre: ingQuery }) }).then(r => r.json()).catch(() => null);
                                  if (claude && claude.calorias !== undefined) setScanConfirmReceta({ nombre: claude.nombre || ingQuery, calorias: claude.calorias, proteina: claude.proteina || 0, carbos: claude.carbos || 0, grasa: claude.grasa || 0, fuente: "api" });
                                  else showToast("No se encontraron datos");
                                }
                              } catch { showToast("Error al buscar en internet"); }
                            }}
                            style={{ padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: COLORS.blue, fontSize: 13, fontWeight: 700 }}
                          >
                            <span>🌐</span><span>Buscar "{ingQuery}" en internet</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Foto receta + foto ingrediente */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    <ImagePicker
                      endpoint="/scan-receta"
                      skipSuccessCheck={true}
                      title="📄 Fotos de receta"
                      description="Añade todas las páginas. Funciona con recetas impresas y escritas a mano."
                      onResult={async (scan) => { await procesarScanResult(scan); }}
                      onError={() => { showToast("Error al procesar la receta"); setPaso("input"); }}
                    >
                      {(triggerOpen, loading) => (
                        <button onClick={triggerOpen} disabled={loading} style={{ flex: 1, padding: "10px 8px", borderRadius: 14, background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)', color: loading ? COLORS.textDim : COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>{loading ? "⏳" : "📄 Foto receta"}</button>
                      )}
                    </ImagePicker>
                    <ImagePicker
                      endpoint="/scan-receta"
                      skipSuccessCheck={true}
                      title="🖼 Foto ingrediente"
                      description="Fotografía el ingrediente o su etiqueta. Puedes añadir varias fotos."
                      onResult={async (scan) => { await procesarScanResult(scan); }}
                      onError={() => { showToast("Error al procesar el ingrediente"); setPaso("input"); }}
                    >
                      {(triggerOpen, loading) => (
                        <button onClick={triggerOpen} disabled={loading} style={{ flex: 1, padding: "10px 8px", borderRadius: 14, background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)', color: loading ? COLORS.textDim : COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>{loading ? "⏳" : "🖼 Foto ingrediente"}</button>
                      )}
                    </ImagePicker>
                  </div>


                                    {/* Lista de ingredientes */}
                  {ingredientes.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ color: COLORS.accent, fontSize: 10, letterSpacing: 2, margin: "0 0 10px", fontWeight: 800 }}>INGREDIENTES ({ingredientes.length})</p>
                      {ingredientes.map((ing, i) => {
                        const isLast = i === ingredientes.length - 1;
                        const unitLabel = getUnitLabel(ing);
                        const cantidadVal = getCantidad(ing);
                        return (
                          <div
                            key={i}
                            ref={isLast ? lastIngRef : null}
                            onClick={() => { if (ing.missing) setMissingLabelIdx(i); }}
                            style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "7px 10px", background: ing.missing ? 'rgba(255,45,85,0.15)' : 'rgba(255,255,255,0.04)', borderRadius: 10, border: `1px solid ${ing.missing ? 'rgba(255,45,85,0.5)' : (isLast ? COLORS.accent + '50' : 'rgba(255,255,255,0.07)')}`, cursor: ing.missing ? 'pointer' : 'default' }}
                          >
                            {ing.missing ? (
                              <>
                                <span style={{ flex: 1, color: '#ff8080', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ing.nombre}</span>
                                <span style={{ color: '#ff2d55', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>📷 SIN DATOS</span>
                                <button onClick={e => { e.stopPropagation(); setIngredientes(p => p.filter((_,j) => j!==i)); }} style={{ background: "none", border: "none", color: "#ff2d5570", fontSize: 16, cursor: "pointer", padding: "2px 4px", lineHeight: 1, flexShrink: 0 }}>✕</button>
                              </>
                            ) : (
                              <>
                                <span style={{ flex: 1, color: COLORS.text, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ing.nombre}</span>
                                <input
                                  ref={isLast ? lastCantidadRef : null}
                                  type="number"
                                  inputMode="decimal"
                                  value={cantidadVal || ""}
                                  onChange={e => handleCantidadChange(e.target.value, i)}
                                  placeholder="0"
                                  style={{ width: 64, background: 'rgba(0,0,0,0.3)', border: `1px solid ${isLast ? COLORS.accent + '60' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8, color: COLORS.accent, fontSize: 15, fontWeight: 800, outline: "none", textAlign: "center", padding: "5px 4px", boxSizing: 'border-box' }}
                                />
                                <span style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 600, minWidth: 24 }}>{unitLabel}</span>
                                <button onClick={() => setIngredientes(p => p.filter((_,j) => j!==i))} style={{ background: "none", border: "none", color: "#ff2d5570", fontSize: 16, cursor: "pointer", padding: "2px 4px", lineHeight: 1, flexShrink: 0 }}>✕</button>
                              </>
                            )}
                          </div>
                        );
                      })}
                      {ingredientes.filter(i => !i.missing).length > 0 && (() => {
                        const validIngs = ingredientes.filter(i => !i.missing);
                        const peso = validIngs.reduce((s, i) => s + (i.gramos || 0), 0);
                        const cal = validIngs.reduce((s, i) => s + i.calorias_100g * i.gramos / 100, 0);
                        const prot = validIngs.reduce((s, i) => s + i.proteina_100g * i.gramos / 100, 0);
                        const carb = validIngs.reduce((s, i) => s + i.carbos_100g * i.gramos / 100, 0);
                        const gras = validIngs.reduce((s, i) => s + i.grasa_100g * i.gramos / 100, 0);
                        const nPorciones = Math.max(1, parseInt(numPorciones) || 1);
                        const divisor = tipoRegistro === "gramos" ? (peso > 0 ? peso / 100 : 1) : nPorciones;
                        const label = tipoRegistro === "gramos" ? "POR 100G" : (nPorciones === 1 ? "TOTAL" : "POR PORCIÓN");
                        return (
                          <div style={{ background: 'rgba(253,139,0,0.07)', border: `1px solid ${COLORS.accent}30`, borderRadius: 12, padding: "10px 14px", marginTop: 6 }}>
                            <p style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1.5, margin: "0 0 8px", fontWeight: 800 }}>MACROS {label} · {r1(peso)}G TOTAL</p>
                            <div style={{ display: "flex", gap: 8 }}>
                              {[["kcal", r1(cal / divisor), COLORS.accent], ["prot", r1(prot / divisor), COLORS.blue], ["carb", r1(carb / divisor), COLORS.green], ["gras", r1(gras / divisor), COLORS.purple]].map(([l, v, c]) => (
                                <div key={l} style={{ flex: 1, textAlign: "center" }}>
                                  <div style={{ color: c, fontWeight: 800, fontSize: 15 }}>{v}</div>
                                  <div style={{ color: COLORS.textMuted, fontSize: 10 }}>{l}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Info extra del scan */}
                  {(metadataExtra.informacion_nutricional || preparacion || metadataExtra.tiempo || metadataExtra.fuente || metadataExtra.notas || metadataExtra.beneficios) ? (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ color: COLORS.text, fontSize: 14, lineHeight: 1.8, margin: 0, whiteSpace: "pre-wrap" }}>
                        {[
                          metadataExtra.informacion_nutricional && "INFORMACIÓN NUTRICIONAL\n" + metadataExtra.informacion_nutricional,
                          preparacion && "PREPARACIÓN\n" + preparacion,
                          metadataExtra.tiempo && "TIEMPO\n" + metadataExtra.tiempo,
                          metadataExtra.fuente && "RECETA DE\n" + metadataExtra.fuente,
                          metadataExtra.notas && "NOTAS\n" + metadataExtra.notas,
                          metadataExtra.beneficios && "BENEFICIOS\n" + metadataExtra.beneficios,
                        ].filter(Boolean).join("\n\n")}
                      </p>
                    </div>
                  ) : null}
                </>
              )}

              {paso === "analizando" && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
                  <p style={{ color: COLORS.text, fontSize: 16, fontWeight: 700 }}>Analizando receta...</p>
                  <p style={{ color: COLORS.textMuted, fontSize: 13 }}>Identificando ingredientes y buscando valores nutricionales</p>
                </div>
              )}

              {paso === "guardando" && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>💾</div>
                  <p style={{ color: COLORS.text, fontSize: 16 }}>Guardando receta...</p>
                </div>
              )}

            </div>{/* fin zona scrollable */}
          </div>
        </MaximizedPopup>
      )}


      {/* Popup confirmar alimento buscado en internet (desde editar receta) */}
      {editScanConfirm && (
        <Popup onClose={() => setEditScanConfirm(null)} title="Confirmar alimento" zIndex={10002}>
          {[["Nombre", "nombre", "text"], ["Calorías (por 100g)", "calorias", "number"], ["Proteína (por 100g)", "proteina", "number"], ["Carbos (por 100g)", "carbos", "number"], ["Grasa (por 100g)", "grasa", "number"]].map(([label, key, type]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>{label.toUpperCase()}</label>
              <input type={type} value={editScanConfirm[key] ?? ""} onChange={e => setEditScanConfirm(p => ({ ...p, [key]: e.target.value }))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
            </div>
          ))}
          <button onClick={async () => {
            const d = editScanConfirm;
            await fetch(API + "/alimentos", { method: "POST", headers: {"Content-Type": "application/json"}, credentials: "include", body: JSON.stringify({ nombre: d.nombre, calorias: parseFloat(d.calorias)||0, proteina: parseFloat(d.proteina)||0, carbos: parseFloat(d.carbos)||0, grasa: parseFloat(d.grasa)||0, fuente: d.fuente || "online" }) }).catch(() => {});
            if (d.idx !== undefined) {
              setEditandoReceta(r => { const ings = [...r.ingredientes]; ings[d.idx] = { ...ings[d.idx], nombre: d.nombre, calorias_100g: parseFloat(d.calorias)||0, proteina_100g: parseFloat(d.proteina)||0, carbos_100g: parseFloat(d.carbos)||0, grasa_100g: parseFloat(d.grasa)||0, fuente: "online" }; return { ...r, ingredientes: ings }; });
              setEditIngEditIdx(null); setEditIngEditQuery(""); setEditIngEditResults([]);
            } else {
              setEditandoReceta(r => ({ ...r, ingredientes: [...r.ingredientes, { nombre: d.nombre, gramos: 100, cantidad: 100, tipo_medida: "peso", peso_unidad: null, densidad: 1.0, nombre_unidad: null, calorias_100g: parseFloat(d.calorias)||0, proteina_100g: parseFloat(d.proteina)||0, carbos_100g: parseFloat(d.carbos)||0, grasa_100g: parseFloat(d.grasa)||0, fuente: "online" }] }));
              setEditIngQuery(""); setEditIngResults([]);
            }
            setEditScanConfirm(null);
            showToast("✅ " + d.nombre + (d.idx !== undefined ? " actualizado" : " añadido"));
          }} style={{ width: "100%", padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>{editScanConfirm.idx !== undefined ? "ACTUALIZAR INGREDIENTE" : "AÑADIR INGREDIENTE"}</button>
        </Popup>
      )}
      {/* Popup confirmar alimento buscado en internet (desde ingredientes) */}
      {scanConfirmReceta && (
        <Popup onClose={() => setScanConfirmReceta(null)} title="Confirmar alimento" zIndex={10001}>
          {[["Nombre", "nombre", "text"], ["Calorías (por 100g)", "calorias", "number"], ["Proteína (por 100g)", "proteina", "number"], ["Carbos (por 100g)", "carbos", "number"], ["Grasa (por 100g)", "grasa", "number"]].map(([label, key, type]) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 1, display: "block", marginBottom: 4 }}>{label.toUpperCase()}</label>
              <input type={type} value={scanConfirmReceta[key] ?? ""} onChange={e => setScanConfirmReceta(p => ({ ...p, [key]: type === "number" ? e.target.value : e.target.value }))} style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", color: COLORS.text, fontSize: 15, outline: "none", boxSizing: "border-box" }} />
            </div>
          ))}
          <button onClick={async () => {
            const d = scanConfirmReceta;
            await fetch(API + "/alimentos", { method: "POST", headers: {"Content-Type": "application/json"}, credentials: "include", body: JSON.stringify({ nombre: d.nombre, calorias: parseFloat(d.calorias)||0, proteina: parseFloat(d.proteina)||0, carbos: parseFloat(d.carbos)||0, grasa: parseFloat(d.grasa)||0, fuente: d.fuente || "online" }) }).catch(() => {});
            setIngredientes(p => [...p, { nombre: d.nombre, gramos: 100, cantidad: 100, tipo_medida: "peso", peso_unidad: null, densidad: 1.0, nombre_unidad: null, calorias_100g: parseFloat(d.calorias)||0, proteina_100g: parseFloat(d.proteina)||0, carbos_100g: parseFloat(d.carbos)||0, grasa_100g: parseFloat(d.grasa)||0, fuente: "online" }]);
            setScanConfirmReceta(null);
            setIngQuery(""); setIngResults([]);
            showToast("✅ " + d.nombre + " añadido");
          }} style={{ width: "100%", padding: 14, borderRadius: 12, background: `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>AÑADIR INGREDIENTE</button>
        </Popup>
      )}
      {/* AlimentoWizard para ingrediente faltante (fondo rojo) */}
      {missingLabelIdx !== null && (
        <AlimentoWizard
          modoNutricion={false}
          zIndex={10000}
          initialNombre={ingredientes[missingLabelIdx]?.nombre || ""}
          onClose={() => setMissingLabelIdx(null)}
          onComplete={async (data) => {
            const idx = missingLabelIdx;
            setMissingLabelIdx(null);
            await fetch(API + "/alimentos", {
              method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
              body: JSON.stringify({
                nombre: data.nombre, calorias: data.calorias, proteina: data.proteina,
                carbos: data.carbos, grasa: data.grasa, fuente: "scan",
                tipo_medida: data.tipo_medida, tipo_cantidad: data.tipo_cantidad,
                peso_unidad: data.peso_unidad, densidad: data.densidad,
              }),
            }).catch(() => {});
            setIngredientes(p => p.map((ing, i) => i !== idx ? ing : {
              ...ing,
              nombre: data.nombre,
              calorias_100g: data.calorias,
              proteina_100g: data.proteina,
              carbos_100g: data.carbos,
              grasa_100g: data.grasa,
              tipo_medida: data.tipo_medida || "peso",
              peso_unidad: data.peso_unidad || null,
              densidad: data.densidad || 1.0,
              missing: false,
            }));
            showToast("✅ " + data.nombre + " añadido a la BD");
          }}
        />
      )}
      {/* Wizard crear ingrediente desde receta */}
      {showWizardManual && (
        <AlimentoWizard
          modoNutricion={false}
          onClose={() => setShowWizardManual(false)}
          onComplete={handleWizardComplete}
        />
      )}
    </div>
  );
}
